import React, { useCallback, useEffect, useState, useRef } from 'react';
import { View, StyleSheet, ActivityIndicator, FlatList, SafeAreaView } from 'react-native';
import { Card, Checkbox } from 'react-native-paper';
import useLeaveTypes from '../../../hooks/useLeaveTypes';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../styles/commonStyles';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import CustomColorPicker from '../../../components/CustomColorPicker';
import { useTableHelper } from '../../../hooks/useTableHelper';

const LeaveTypeRow = React.memo(({ item, index, styles, handleColourChange, handleEditPress, handleDeleteLeaveType, t, }) => {
    const [showMoreInfo, setShowMoreInfo] = useState(false);
    const itemColour = item.colour.toLowerCase() || '#86BC25';
    return (
        <SafeAreaView style={styles.item}>
            <SafeAreaView style={styles.row}>
                <View>
                    <CustomColorPicker
                        initialColor={itemColour}
                        onColorChange={(newColor) => handleColourChange(index, newColor)}
                    />
                </View>

                <SafeAreaView style={styles.inputContainer}>
                    <CustomInput
                        value={item.aCode}
                        editable={true}
                        selectall={true}
                        setValue={(e) => handleEditPress(index, item, 'aCode', e)}
                        maxLen={255}
                        type="MAININPUT"
                        style={[styles.codeInput]}
                    />
                </SafeAreaView>
                <SafeAreaView style={styles.inputContainer}>
                    <CustomInput
                        value={item.description}
                        editable={true}
                        selectall={true}
                        setValue={(e) => handleEditPress(index, item, 'description', e)}
                        maxLen={255}
                        type="MAININPUT"
                        style={[styles.input]}
                    />
                </SafeAreaView>
                <SafeAreaView style={styles.checkboxContainer}>
                    <Checkbox
                        status={item.isNegativeBalanceAllowed ? 'checked' : 'unchecked'}
                        onPress={() => handleEditPress(index, item, 'isNegativeBalanceAllowed', !item.isNegativeBalanceAllowed)}
                        color={item.isNegativeBalanceAllowed ? '#86BC25' : undefined}
                    />
                    <CustomLabel value={t('common:allowNegativeBalance')} type="CHECKBOXLABEL" />
                </SafeAreaView>
                <SafeAreaView style={styles.checkboxContainer}>
                    <Checkbox
                        status={item.isDocRequired ? 'checked' : 'unchecked'}
                        onPress={() => handleEditPress(index, item, 'isDocRequired', !item.isDocRequired)}
                        color={item.isDocRequired ? '#86BC25' : undefined}
                    />
                    <CustomLabel value={t('common:requireAttachment')} type="CHECKBOXLABEL" />
                </SafeAreaView>
                <CustomButton
                    onPress={() => setShowMoreInfo(!showMoreInfo)}
                    text={showMoreInfo ? 'Hide' : 'More'}
                    type="TIMESHEET"
                />
            </SafeAreaView>
            <View style={styles.separator} />
            {showMoreInfo && (
                <SafeAreaView style={styles.row}>
                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.cancreateleavescheme ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'cancreateleavescheme', !item.cancreateleavescheme)}
                                color={item.cancreateleavescheme ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:createLeaveScheme')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.ignoreLeaveSchemeRules ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'ignoreLeaveSchemeRules', !item.ignoreLeaveSchemeRules)}
                                color={item.ignoreLeaveSchemeRules ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:ignoreLeaveSchemeRules')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isPublicHoliday ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isPublicHoliday', !item.isPublicHoliday)}
                                color={item.isPublicHoliday ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:publicHoliday')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isWellbeingDay ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isWellbeingDay', !item.isWellbeingDay)}
                                color={item.isWellbeingDay ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:wellbeingDay')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isDocAllowed ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isDocAllowed', !item.isDocAllowed)}
                                color={item.isDocAllowed ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:allowAttachment')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                    </SafeAreaView>

                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.ignoreSaturdays ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'ignoreSaturdays', !item.ignoreSaturdays)}
                                color={item.ignoreSaturdays ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:ignoreSaturdays')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.ignoreSundays ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'ignoreSundays', !item.ignoreSundays)}
                                color={item.ignoreSundays ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:ignoreSundays')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.ignoreWellbeingDays ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'ignoreWellbeingDays', !item.ignoreWellbeingDays)}
                                color={item.ignoreWellbeingDays ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:ignoreWellbeingDays')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.ignorePublicHolidays ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'ignorePublicHolidays', !item.ignorePublicHolidays)}
                                color={item.ignorePublicHolidays ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:ignorePublicHolidays')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                    </SafeAreaView>

                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.cancf ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'cancf', !item.cancf)}
                                color={item.cancf ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:canCF')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.displOnPlan ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'displOnPlan', !item.displOnPlan)}
                                color={item.displOnPlan ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:showOnScheduler')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.updhol ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'updhol', !item.updhol)}
                                color={item.updhol ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:cumulative')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.calendarDays ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'calendarDays', !item.calendarDays)}
                                color={item.calendarDays ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:calendarDays')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                       
                    </SafeAreaView>

                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isCustomAllowance ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isCustomAllowance', !item.isCustomAllowance)}
                                color={item.isCustomAllowance ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:customAllowance')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isChangeOfRule ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isChangeOfRule', !item.isChangeOfRule)}
                                color={item.isChangeOfRule ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:changeOfRule')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.UseRestrictLAWithinPeriod ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'UseRestrictLAWithinPeriod', !item.UseRestrictLAWithinPeriod)}
                                color={item.UseRestrictLAWithinPeriod ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:restrictWithinPeriod')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                        <SafeAreaView style={styles.checkboxContainer}>
                            <Checkbox
                                status={item.isLARequired ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, 'isLARequired', !item.isLARequired)}
                                color={item.isLARequired ? '#86BC25' : undefined}
                            />
                            <CustomLabel value={t('common:laRequired')} type="CHECKBOXLABEL" />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            )}
            {showMoreInfo && (
                <>
                    <View style={styles.separator} />
                    <SafeAreaView style={styles.row}>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:setMonthsNotification')} type="TIMESHEET" />
                            <CustomInput
                                value={item.enableWarningInMonths}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'enableWarningInMonths', e)}
                                keyboardType="numeric"
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:attachmentRequiredAfterUnits')} type="TIMESHEET" />
                            <CustomInput
                                value={item.docRequiredAfterUnits}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'docRequiredAfterUnits', e)}
                                keyboardType="numeric"
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                    <SafeAreaView style={styles.row}>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:cfExpiry')} type="TIMESHEET" />
                            <CustomInput
                                value={item.cfexpdate}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'cfexpdate', e)}
                                maxLen={255}
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:balanceExceededMsg')} type="TIMESHEET" />
                            <CustomInput
                                value={item.balanceexceededmsg}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'balanceexceededmsg', e)}
                                maxLen={255}
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:submittedMessage')} type="TIMESHEET" />
                            <CustomInput
                                value={item.submitMsg}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'submitMsg', e)}
                                maxLen={255}
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.inputContainer}>
                            <CustomLabel value={t('common:daysBefore')} type="TIMESHEET" />
                            <CustomInput
                                value={item.daysBefore}
                                editable={true}
                                selectall={true}
                                setValue={(e) => handleEditPress(index, item, 'daysBefore', e)}
                                maxLen={255}
                                type="MAININPUT"
                                style={[styles.input, { width: item.description.length * 10 }]}
                            />
                        </SafeAreaView>
                        <SafeAreaView>
                            <CustomButton
                                iconName='trash-outline'
                                onPress={() => handleDeleteLeaveType(item, index, 'rowguid')}
                                show='HOME_BELL'
                                type='HOME_BELL'
                                textTop='0'
                                sz={30}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </>
            )}
        </SafeAreaView>
    );

}, (prev, next) => prev.item === next.item);

const HRAdminLeaveTypesScreen = ({ selectedCompany, userid }) => {
    const { isLoading, error, GetLeaveTypes, SaveLeaveType, DeleteLeaveType, } = useLeaveTypes();
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [initialLeaveTypes, setInitialLeaveTypes] = useState([]);
    const [changes, setChanges] = useState({});
    const [deletedItems, setDeletedItems] = useState([])
    const [isDuplicate, setIsDuplicate] = useState([]);
    const [initialIsDuplicate, setInitialIsDuplicate] = useState([]);
    const [hasNotProvidedCode, setHasNotProvidedCode] = useState([]);
    const [initialHasNotProvidedCode, setInitialHasNotProvidedCode] = useState([]);
    const [leaveTypesCnt, setLeaveTypesCnt] = useState(0);
    const [initialLeaveTypesCnt, setInitialLeaveTypesCnt] = useState(0);
    const { rowEdit, rowAdd, rowDelete } = useTableHelper();
    const ref = useRef(null);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const { t } = useTranslation();

    const fetchLeaveTypes = async () => {
        const data = await GetLeaveTypes(selectedCompany);
        setLeaveTypes(data);
        setInitialLeaveTypes(data);
        setLeaveTypesCnt(data?.length);
        setInitialLeaveTypesCnt(data?.length);
        setIsDuplicate(new Array(data?.length).fill(false));
        setInitialIsDuplicate(new Array(data?.length).fill(false));
        setHasNotProvidedCode(new Array(data?.length).fill(false));
        setInitialHasNotProvidedCode(new Array(data?.length).fill(false));
    };
    useEffect(() => {

        if (selectedCompany) {
            fetchLeaveTypes();
        }
    }, [selectedCompany]);

    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, leaveTypes, setLeaveTypes);
    }, []);

    const handleDeleteLeaveType = (item, index, attribute) => {
        try {
            rowDelete(item, index, attribute, changes, setChanges, leaveTypes, setLeaveTypes, setDeletedItems, ref)
        } catch (error) {
            console.error('Error deleting leave type:', error);
        }
    };

    const handleAddNewLeaveType = () => {
        rowAdd(leaveTypes, setLeaveTypes, ['rowguid'],
            ['updhol',
                'dispOnPlan',
                'isLARequired',
                'cancreateleavescheme',
                'ignoreLeaveSchemeRules',
                'ignoreSaturdays',
                'ignoreSundays',
                'ignorePublicHolidays',
                'ignoreWellbeingDays',
                'cancf', 'isPublicHoliday',
                'isWellbeingDay',
                'isDocAllowed',
                'isDocRequired',
                'isNegativeBalanceAllowed',
                'calendarDays', 'isCustomAllowance',
                'isChangeOfRule',
                'enableWarningInMonths',
                'UseRestrictLAWithinPeriod'], ref, t, ['daysBefore',
            'docRequiredAfterUnits'],
            [], changes, setChanges)
    };

    const removeFromChangesAfterSave = () => {
        setChanges({})
        setDeletedItems([])
    }

    const handleSaveLeaveTypes = async () => {
        let newPromises = [];
        let deleteSucc = false;
        let updateSucc = false;
        let deleteMessage = 'Failed to Delete';

        // console.log(deletedItems.length)
        // console.log(Object.entries(changes).length)
        // * Do not save if Changes table is empty
        if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "danger",
                duration: 3000,
            })
            return
        }

        //* Check that all aCodes are unique, in these scenarion only that matter the rest can be the same 
        // Check uniqueness of aCode
        const allACodesUnique = new Set(leaveTypes.map(item => item.aCode)).size === leaveTypes.length;
        // Check uniqueness of description
        const allDescriptionsUnique = new Set(leaveTypes.map(item => item.description)).size === leaveTypes.length;
        // console.log('All aCode values unique:', allACodesUnique);
        // console.log('All description values unique:', allDescriptionsUnique);

        if (!allACodesUnique || !allDescriptionsUnique) {
            showMessage({
                message: t('common:leaveCodeandDesc'),
                type: "danger",
                duration: 3000,
            })
            return null;
        }

        //#region Create Promise
        if (Object.entries(changes).length > 0) {
            newPromises.push(
                SaveLeaveType(changes, selectedCompany)
                    .then((res) => {
                        if (res) {
                            updateSucc = true;
                            removeFromChangesAfterSave();
                        }

                    })
                    .catch(() => { }))
        }

        if (deletedItems.length > 0) {
            newPromises.push(DeleteLeaveType(deletedItems, selectedCompany)
                .then((res) => {
                    console.log(res)
                    if (res) {
                        deleteSucc = res
                        removeFromChangesAfterSave();
                    }
                }).catch((e) => {
                    // * The errror message is handled from the API
                    deleteMessage = e;
                }))
        }

        //#endregion
        // * Send the Changes table in the API which only includes Updated or new Lines
        // * No check in the API if row changed. 
        try {
            await Promise.all(newPromises).then(() => {
                if (!updateSucc && !deleteSucc) {
                    if (!deleteSucc) {
                        showMessage({
                            message: deleteMessage,
                            type: "danger",
                            duration: 3000,
                        })
                    }
                    if (!updateSucc)
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "error",
                            duration: 3000,
                        })

                }
                else if (deleteSucc || updateSucc) {
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    })
                }
                fetchLeaveTypes();
            });
        } catch (error) {
            console.log(error)
        }
    }

    const handleCancelChanges = () => {
        setLeaveTypes(initialLeaveTypes);
        setChanges({});
        setDeletedItems([]);
        setIsDuplicate(initialIsDuplicate);
        setHasNotProvidedCode(initialHasNotProvidedCode);
        setLeaveTypesCnt(initialLeaveTypesCnt);
    };

    const handleColourChange = (index, newColor) => {
        setLeaveTypes(prevLeaveTypes => {
            const updatedLeaveTypes = [...prevLeaveTypes];
            updatedLeaveTypes[index].colour = newColor;
            updatedLeaveTypes[index].isModified = true;
            return updatedLeaveTypes;
        });
    };

    const renderFunctions = useCallback((item, index) => {
        return (
            <Card style={common.cardstyle}>
                <Card.Content>
                    <LeaveTypeRow item={item} index={index}
                        styles={styles}
                        handleColourChange={handleColourChange}
                        handleEditPress={handleEditPress}
                        handleDeleteLeaveType={handleDeleteLeaveType}
                        t={t}
                    />
                </Card.Content>
            </Card>
        )
    }, [leaveTypes]);

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={[styles.buttons, styles.functionalityButtons]}>
                <CustomButton
                    text={t('common:createNewFunction')}
                    onPress={handleAddNewLeaveType}
                    type='BUTTON_ADD'
                    textTop='0'
                    sz={15}
                />
                <CustomButton
                    text={t('common:saveFunction')}
                    onPress={handleSaveLeaveTypes}
                    type='BUTTON_SAVE'
                    textTop='0'
                    isGradient={true}
                    sz={15}
                />
                <CustomButton
                    text={t('common:cancel')}
                    onPress={handleCancelChanges}
                    type="BUTTON_CANCEL"
                    textTop='0'
                    sz={15}
                />
            </View>
            <SafeAreaView style={common.divider} />
            <FlatList
                data={leaveTypes}
                keyExtractor={(item, index) => `${item.rowguid}-${index}`}
                renderItem={({ item, index }) => renderFunctions(item, index)}
                scrollEnabled={true}
                refreshing={isLoading}
                ref={ref}
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel value={t('common:code')} type="SUBTITLE" />
                            <CustomLabel value={t('common:description')} type="SUBTITLE" />
                            <View style={styles.spacer} />
                            <View style={styles.checkboxContainer} />
                        </SafeAreaView>
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel value={t("common:functionsNoDataPreview")} type="NO_FUNCTIONS" />
                }
                initialNumToRender={4}
            />

        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    error: {
        color: 'red',
        fontSize: 16,
    },
    item: {
        flex: 1,
        width: '98%',
        padding: 0,
        marginHorizontal: 5,
        justifyContent: 'center',
        alignItems: 'left',
        textAlign: 'left',
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginHorizontal: 5,
    },
    spacer: {
        flex: 1,
    },
    column: {
        flex: 1,
    },
    separator: {
        height: 1,
        backgroundColor: '#86BC25',
        marginVertical: 10,
        width: '100%',
    },
    colorPicker: {
        width: 40,
        height: 40,
        backgroundColor: '#86BC25',
        borderRadius: 5,
        marginRight: 10,
    },
    inputContainer: {
        flex: 1,
        marginHorizontal: 5,
        alignItems: 'left',
        textAlign: 'left',
    },
    checkboxContainer: {
        flexDirection: 'row',
        marginVertical: 5,
    },
    input: {
        paddingHorizontal: 5,
        paddingVertical: 5,
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 5,
        backgroundColor: '#f9f9f9',
        textAlign: 'left',
        flexDirection: 'row',
    },
    codeInput: {
        paddingHorizontal: 5,
        paddingVertical: 5,
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 5,
        backgroundColor: '#f9f9f9',
        textAlign: 'left',
    },
    moreInfo: {
        marginTop: 10,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignSelf: 'flex-end',
        alignItems: 'center',
        justifyContent: 'center',
    },

});

export default HRAdminLeaveTypesScreen;